<template>
  <div class="o-billingAddress">
    <BlockHeader title="Billing address" faIconClass="fa fas fa-user-edit" rightText="* required fields"/>
    <div v-show="showBillingDetailsForm">
      <div class="o-billingAddress--container">
        <form @submit.prevent="validateForm('invoice-form')" data-vv-scope="invoice-form">
          <fieldset v-bind:disabled="isUpdatingAddress">
            <div class="o-billingAddress--container__colrow">
              <InputText
                labelText="First name*"
                name="first-name"
                autocomplete="name"
                :minlength="1"
                :maxlength="255"
                :required="true"
                :errorMessage="errors.has('invoice-form.first-name') ? 'Required' : ''"
                :successState="!errors.has('invoice-form.first-name')"
                v-model="invoiceFirstName"
                data-vv-as="First name"
                v-validate="'required'"
              />
              <InputText
                labelText="Last name*"
                name="last-name"
                autocomplete="family-name"
                :minlength="1"
                :maxlength="255"
                :required="true"
                :errorMessage="errors.has('invoice-form.last-name') ? 'Required' : ''"
                :successState="!errors.has('invoice-form.last-name')"
                v-model="invoiceLastName"
                data-vv-as="Last name"
                v-validate="'required'"
              />
            </div>
            <ToggleText
              :class="'o-billingAddress--container__optionaltoggle'"
              :faIconClass="showOptionalBillingDetails ? 'fas fa-minus' : 'fas fa-plus'"
              text="Optional billing details"
              v-on:toggleEditable="toggleOptionalBillingDetails"
            />
            <div class="o-billingAddress--container__optionalfields" v-show="showOptionalBillingDetails">
              <InputText
                labelText="Company name"
                name="company"
                autocomplete="organization"
                :maxlength="50"
                v-model="invoiceCompany"
                data-vv-as="Company"
              />
              <InputText
                labelText="Address line 1"
                name="address-1"
                autocomplete="address-line1"
                :maxlength="255"
                v-model="invoiceAddress1"
                data-vv-as="Address Line 1"
              />
              <InputText
                labelText="Address line 2"
                name="address-2"
                autocomplete="address-line2"
                :maxlength="255"
                v-model="invoiceAddress2"
                data-vv-as="Address Line 2"
              />
              <InputText
                labelText="City"
                name="city"
                autocomplete="address-line2"
                :maxlength="255"
                v-model="invoiceCity"
                data-vv-as="City"
              />
            </div>
            <div class="o-billingAddress--container__colrow" v-show="showStateAndZipCode">
              <div v-if="availableRegions.length > 0" :class="{ 'a-input-select a-input-text-div__invoiceState': showPostCode }">
                <label for="invoiceState" class="a-input-select__label">{{regionLabel}}</label>
                <select
                  id="invoiceState"
                  name="invoiceState"
                  :close-on-select="true"
                  :show-labels="false"
                  v-model="invoiceState"
                  v-on:keypress="disableKeyDown"
                  :required="isSelectedCountryUSOrCA"
                  v-validate="{ required: isSelectedCountryUSOrCA }"
                  class="a-input-select__select"
                  :class="errors.has('invoice-form.invoiceState') ? 'a-input--danger' : 'a-input--success'"
                >
                  <option
                    v-for="(region, index) in availableRegions"
                    v-bind:key="index"
                    v-bind:value="region"
                    class="a-input-select__select__options"
                  >
                    {{ region }}
                  </option>
                </select>
                <p v-if="errors.has('invoice-form.invoiceState')" class="a-input__error-message">Required</p>
              </div>
              <InputText
                v-if="showPostCode"
                :labelText="postcodeLabel"
                name="postcode"
                autocomplete="postal-code"
                :maxlength="10"
                :required="isSelectedCountryUSOrCA"
                :errorMessage="errors.has('invoice-form.postcode') || errorZipCode ? 'Please enter a valid ZIP code': ''"
                :successState="!errors.has('invoice-form.postcode') && !errorZipCode"
                v-validate="zipCodeValidationConfig"
                v-on:keypress="isAlpha"
                v-model="invoicePostCode"
                data-vv-as="Zip/Postcode"
              />
            </div>
            <div class="a-input-select">
              <label for="country-list" class="a-input-select__label">Country*</label>
              <select
                id="country-list"
                name="country-list"
                autocomplete="country"
                v-model="selectedCountry"
                data-vv-as="Country"
                v-validate="'required'"
                placeholder="Country (required)"
                v-on:keypress="disableKeyDown"
                class="a-input-select__select"
                :class="errors.has('invoice-form.country-list') ? 'a-input--danger' : 'a-input--success'"
              >
                <option
                  v-for="country in countries"
                  v-bind:key="country.country_id"
                  v-bind:value="country.code"
                  class="a-input-select__select__options"
                >
                  {{ country.name }}
                </option>
              </select>
              <p v-if="errors.has('invoice-form.country-list')" class="a-input__error-message">Required</p>
            </div>
          </fieldset>
        </form>
      </div>
    </div>
    <div v-show="!showBillingDetailsForm">
      <invoice-details v-on:toggleEditable="toggleBillingDetailsForm"/>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { isoCountries } from '../../store/modules/countrylist'
import { regionList } from '../../store/modules/regionlist'
import InvoiceDetails from '../molecules/InvoiceDetails.vue'
import BlockHeader from '../molecules/BlockHeader.vue'
import InputText from '../atoms/InputText.vue'
import ToggleText from '../atoms/ToggleText.vue'

export default {
  computed: mapState({
    firstName: state => state.cart.firstName,
    lastName: state => state.cart.lastName,
    address1: state => state.cart.address1,
    address2: state => state.cart.address2,
    city: state => state.cart.city,
    state: state => state.cart.state,
    postcode: state => state.cart.postcode,
    country: state => state.cart.country,
    countryCode: state => state.cart.countryCode,
    company: state => state.cart.company,
    postcodeLabel () {
      if (this.isSelectedCountryUS) {
        return 'ZIP code*'
      }
      return this.isSelectedCountryCA ? 'Postcode*' : 'Postcode'
    },
    regionLabel () {
      if (this.isSelectedCountryUS) {
        return 'State*'
      }

      return this.isSelectedCountryCA ? 'Province*' : 'Region'
    },
    showStateAndZipCode () {
      // only display zipcode and state if selected country is US
      // or if it's not US and we have toggle the show optionalBillingDetails
      return (this.isSelectedCountryUSOrCA || (!this.isSelectedCountryUS && this.showOptionalBillingDetails))
    },
    isSelectedCountryUS () {
      return this.selectedCountry === 'US'
    },
    isSelectedCountryCA () {
      return this.selectedCountry === 'CA'
    },
    isSelectedCountryUSOrCA () {
      return this.isSelectedCountryUS || this.isSelectedCountryCA
    },
    showPostCode () {
      return this.showOptionalBillingDetails || this.isSelectedCountryUSOrCA
    },
    zipCodeValidationConfig () {
      const validationConfig = { required: this.isSelectedCountryUSOrCA }
      if (this.isSelectedCountryUS) {
        validationConfig.regex = /^([0-9]{5})(?:[-\s]*([0-9]{4}))?$/
      }
      if (this.isSelectedCountryCA) {
        // Making sure the length is at least 6 characters
        validationConfig.min = 6
      }
      return validationConfig
    }
  }),

  data () {
    return {
      showBillingDetailsForm: false,
      showOptionalBillingDetails: false,
      countries: isoCountries,
      regions: regionList,
      invoiceFirstName: '',
      invoiceLastName: '',
      invoiceAddress1: '',
      invoiceAddress2: '',
      invoiceCompany: '',
      invoiceCity: '',
      invoiceState: '',
      invoicePostCode: '',
      selectedCountry: '',
      selectedCountryName: '',
      availableRegions: [],
      isUpdatingAddress: false,
      errorZipCode: false,
      updateAddressTimeout: 0,
      testInput: ''
    }
  },
  provide () {
    return { parentValidator: this.$validator }
  },
  components: {
    InvoiceDetails,
    InputText,
    ToggleText,
    BlockHeader
  },
  watch: {
    selectedCountry () {
      this.selectedCountryName = this.getCountryName()
      this.availableRegions = this.getRegionList()
      this.$nextTick(() => {
        if (
          this.availableRegions.length === 0 ||
          (this.availableRegions.length > 0 && !this.availableRegions.find(region => region === this.invoiceState))
        ) {
          this.invoiceState = ''
        }
        if (!this.isSelectedCountryUS && !this.isSelectedCountryCA) {
          // we need to reset the validation and remove errors for postcode and region
          // when we select another country than US
          // This will only remove the `success` green border underneath to keep it as default.
          // UI team don't want this border to be green as those fields become optional for non us country
          this.resetValidorField('invoiceState')
          this.resetValidorField('postcode')
        }
        if (this.selectedCountry !== this.$store.state.cart.countryCode) {
          if (this.isSelectedCountryUSOrCA) {
            this.onAddressChangeForUSorCa()
          } else {
            this.saveAddress()
          }
        }
      })
    },
    invoicePostCode () {
      if (this.isSelectedCountryUSOrCA) {
        this.onAddressChangeForUSorCa()
      }
    },
    invoiceState () {
      if (this.invoiceState !== this.$store.state.cart.state) {
        if (this.isSelectedCountryUSOrCA) {
          this.onAddressChangeForUSorCa()
        }
      } else {
        // IF the `state` is same as the user profile's `state`, we should clear any errors related to zipcode because
        // that means that it is valid in the first place. Any changes to state will trigger address update in the
        // above if condition.
        this.errorZipCode = false
        this.resetValidorField('postcode')
      }
    },
    state () {
      this.prepopulateAddressFields()
    }
  },
  mounted () {
    this.prepopulateAddressFields()
    this.showBillingDetailsForm = !this.checkIfCartAddressIsValid()
  },
  methods: {
    resetValidorField (inputName) {
      const field = this.$validator.fields.find({ name: inputName })
      if (field) {
        field.reset()
        this.$validator.errors.remove(field.name, field.scope)
        const input = document.getElementsByName(inputName)
        if (input && input.length > 0) {
          input[0].classList.remove('a-input--success')
          input[0].classList.remove('a-input--danger')
        }
      }
    },
    getCountryName () {
      return this.selectedCountry ? this.countries.find(c => c.code === this.selectedCountry).name : ''
    },
    toggleBillingDetailsForm () {
      this.showBillingDetailsForm = !this.showBillingDetailsForm
    },
    toggleOptionalBillingDetails () {
      this.showOptionalBillingDetails = !this.showOptionalBillingDetails
    },
    prepopulateAddressFields () {
      this.invoiceFirstName = this.firstName
      this.invoiceLastName = this.lastName
      this.invoiceAddress1 = this.address1
      this.invoiceAddress2 = this.address2
      this.invoiceCity = this.city
      this.invoiceState = this.state
      this.invoicePostCode = this.postcode
      this.selectedCountry = this.countryCode
      this.selectedCountryName = this.getCountryName()
      this.invoiceCompany = this.company
    },
    getAddress () {
      return {
        firstName: this.invoiceFirstName,
        lastName: this.invoiceLastName,
        address1: this.invoiceAddress1,
        address2: this.invoiceAddress2,
        city: this.invoiceCity,
        state: this.invoiceState,
        postcode: this.invoicePostCode,
        countryCode: this.selectedCountry,
        company: this.invoiceCompany
      }
    },
    validateForm () {
      return this.$validator.reset().then(() => {
        return this.$validator.validateAll('invoice-form').then(result => {
          this.scrollViewToFormError()
          return result && !this.errorZipCode
        })
      })
    },
    onAddressChangeForUSorCa () {
      // this method is called, on select a Country, change PostCode and change State
      this.$validator.validate('invoice-form.postcode').then(isValidPostCode => {
        if (isValidPostCode) {
          this.$validator.validate('invoice-form.invoiceState').then(isValidState => {
            if (isValidState) {
              // we want to wait 1.5sec once the zip code is valid before doing the api call
              this.updateAddressTimeout = setTimeout(() => {
                this.saveAddress()
              }, 1500)
            } else {
              this.scrollViewToFormError()
            }
          })
        } else {
          // need to clear timeoout in case you set a zipcode valid (5characters)
          // and then delete one character and add a new one quickly
          // to avoid sending multiple requests to ecom
          clearTimeout(this.updateAddressTimeout)
        }
      })
    },
    scrollViewToFormError () {
      // scroll to the first error -- on VeeValidate error we add error-msg to the label
      this.$nextTick(() => {
        const elementsInErrors = document.getElementsByClassName('a-input__error-message')
        if (elementsInErrors && elementsInErrors.length > 0) {
          this.showBillingDetailsForm = true
          elementsInErrors[0].scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
          // checking if there is a for to point into an input. And if yes we get the element and put focus on it.
          if (elementsInErrors[0].htmlFor !== '') {
            const input = document.getElementById(elementsInErrors[0].htmlFor)
            if (input) {
              input.focus()
            }
          }
        }
      })
    },
    async saveAddress () {
      const data = this.getAddress()
      this.isUpdatingAddress = true
      const response = await this.$store.dispatch('cart/updateAddress', {
        useraddress: data
      })
      if (
        response !== undefined && response !== null &&
        response.zipCodeError !== undefined &&
        response.zipCodeError !== '' &&
        this.isSelectedCountryUS
      ) {
        this.showBillingDetailsForm = true
        this.errorZipCode = true
        this.scrollViewToFormError()
      } else {
        this.errorZipCode = false
      }
      this.isUpdatingAddress = false
      return response
    },
    getRegionList () {
      if (this.selectedCountry !== '' && this.regions[this.selectedCountry] !== undefined) {
        const availableRegions = this.regions[this.selectedCountry]
        return availableRegions
      }
      return []
    },
    checkIfCartAddressIsValid () {
      const regions = this.getRegionList()
      return (
        regions &&
        this.firstName !== '' &&
        this.lastName !== '' &&
        (regions.length === 0 || this.state !== '') &&
        this.postcode !== '' &&
        this.countryCode !== ''
      )
    },
    isAlpha (event) {
      if (/^[a-zA-Z0-9- ]+$/.test(event.key)) {
        return
      } else {
        event.preventDefault()
      }
    },
    disableKeyDown (event) {
      if (event) {
        event.preventDefault()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// BillingAddress.vue?26eaddd2